<template>
  <div class="network-manager">

    <!-- Offline mode management -->
    <div
      v-if="offlineMode || slowXhrs.length > 0 || promptNetworkSeemsUnavailable"
      class="network-manager__head v-centerer"
      :class="headClasses"
    >
      <div v-if="offlineMode" class="v-centerer network-manager__head__wrapper">
        <span>{{ $t('message.OFFLINE_MODE_ON') }}</span>
        <button @click="promptSyncOfflineMode">
          {{ $t('message.OFFLINE_MODE_SYNC') }}
        </button>
      </div>

      <div v-else class="v-centerer network-manager__head__wrapper">
        <span>{{ $t('message.OFFLINE_MODE_SLOW_REQUESTS') }}</span>
        <div class="v-centerer">
          <button @click="enableOfflineMode">
            {{ $t('message.OFFLINE_MODE_TURN_ON') }}
          </button>
          <Icon v-if="promptNetworkSeemsUnavailable" @click.native="promptNetworkSeemsUnavailable = false">
            {{ close }}
          </Icon>
        </div>
      </div>
    </div>

    <!-- Loading anmation -->
    <transition name="fade" mode="out-in">
      <div v-show="xhrs.length > 0" class="network-manager__loading">
        <img :src="shizenLogo" alt="">
        <div class="network-manager__loading__number">
          {{ displayedNumber }}
        </div>
      </div>
    </transition>

    <!-- Turning offline mode off, syncing all XHRs that require syncing -->
    <Modal :value="promptSync || syncing || syncingOver" class="network-manager__sync-modal" @input="closeSyncModal">
      <div v-if="promptSync" class="network-manager__sync-modal__prompt">
        <div>
          {{ $t('message.OFFLINE_MODE_PROMPT_OFF') }}
        </div>
        <div v-if="xhrsToSync.length > 0">
          {{ $t('message.OFFLINE_MODE_PROMPT_OFF_NEED_SYNC') }}
        </div>
        <div class="network-manager__sync-modal__prompt__buttons">
          <button class="generic-border generic-border--text-btn generic-border--blue" @click="doSyncOfflineMode">
            {{ $t('message.YES') }}
          </button>
          <button class="generic-border generic-border--text-btn generic-border--red" @click="dontSyncOfflineMode">
            {{ $t('message.NO') }}
          </button>
        </div>
      </div>
      <div v-else class="network-manager__sync-modal__sync">
        <div v-if="syncing" class="v-centerer">
          <div>{{ $t('message.OFFLINE_MODE_SYNCING') }}</div>
          <button class="generic-border generic-border--text-btn generic-border--red" @click="cancelSync">
            {{ $t('message.CANCEL') }}
          </button>
        </div>
        <div v-else-if="syncingOver">
          {{ $t('message.OFFLINE_MODE_SYNCING_OVER') }}
        </div>
        <div
          v-for="xhr in xhrsToSync"
          :key="xhr.xhrUniqueID"
          class="network-manager__sync-modal__sync__xhr v-centerer"
        >
          <div>{{ $t(xhr.info.infoTr) }}</div>
          <div v-if="xhr.syncing" class="network-manager__sync-modal__sync__xhr__loading">
            <img :src="shizenLogo" alt="">
          </div>
          <div v-else class="network-manager__sync-modal__sync__xhr__done">
            <Icon>{{ tickExtraBold }}</Icon>
          </div>
        </div>
        <div class="h-v-centerer network-manager__sync-modal__sync__buttons">
          <button class="generic-border generic-border--text-btn generic-border--blue" @click="closeSyncModal">
            {{ $t('message.CLOSE') }}
          </button>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script>
/**
 * Network manager.
 * - Displays a circular loading indication when an XHR is loading.
 * - Also prompts the user when the network seems unavailable, to switch to offline mode.
 *
 */

import axios from 'axios';
// Components
import Modal from '@/components/modal.vue';
import Icon from '@/components/icon.vue';
// Utils
import { ENABLE_OFFLINE_MODE } from '@/store/modules/device/mutation-types';
import { shizenLogo } from '@/utils/main';
import { globalEventDispatcher, ShizenGlobalEvent } from '@/utils/event-dispatcher/global';
import { close, tickExtraBold } from '@/assets/icons';
import { urlMatchesInfoObject } from '@/utils/network-manager-utils';

const slowXhrWarnTimeout = 240 * 1000;

export default {

  name: 'NetworkManager',

  components: {
    Icon,
    Modal,
  },

  data() {
    return {
      close,
      tickExtraBold,
      shizenLogo,
      // IDs of the ongoing XHR
      xhrs: [],
      // IDs of all the XHR of the series. A series is created at the beginning of a first XHR and
      // stopped when the length of xhrs reach 0
      xhrsOutOf: [],
      // XHRs are added to this array when they reach the time limit defined in slowXhrWarnTimeout
      // They should be removed from the array once they finish
      slowXhrs: [],
      // When offline mode is enabled, remember the XHRs to sync in this array.
      xhrsToSync: [],
      // Timeout started at the beginning of each XHR to warn the user for slow requests.
      timeoutsPerXhrIds: {},
      // Prompts the user that we received hints that the network might be unavailable
      promptNetworkSeemsUnavailable: false,
      promptNetworkUnavailableTimeout: null,
      // True when turning offline mode off, to sync all XHRs that needs syncing
      syncing: false,
      syncingOver: false,
      promptSync: false,
      xhrsToSyncPromises: [],
    };
  },

  computed: {
    /** String: Number to display inside the loading animation */
    displayedNumber() {
      if (this.xhrsOutOf.length > 0) {
        return `${this.xhrsOutOf.length - this.xhrs.length} / ${this.xhrsOutOf.length}`;
      }
      return '';
    },
    /** Object: Classes applied to the header */
    headClasses() {
      return {
        'network-manager__head--warn': this.slowXhrs.length > 0 || this.promptNetworkSeemsUnavailable,
        'network-manager__head--offline': this.offlineMode,
      };
    },
    /** Boolean: Is offline mode activated */
    offlineMode() {
      return this.$store.state.device.offlineMode;
    },
  },

  created() {
    globalEventDispatcher.register(ShizenGlobalEvent.XHR_STARTED, this.xhrStarted);
    globalEventDispatcher.register(ShizenGlobalEvent.XHR_ENDED, this.xhrEnded);
  },

  methods: {
    /** Handle the beginning of an XHR */
    xhrStarted({ config }) {
      // Prevent registering XHR during synchronization
      if (config.syncing) {
        return;
      }
      if (this.offlineMode) {
        // Remember only relevant XHR for syncing
        config = urlMatchesInfoObject(config);
        if (config.info !== undefined) {
          this.xhrsToSync = [...this.xhrsToSync, config];
        }
      } else {
        // Add a timeout that will trigger the slow request warning
        this.timeoutsPerXhrIds[`${config.xhrUniqueID}`] = setTimeout(() => {
          this.slowXhrs = [...this.slowXhrs, config];
        }, slowXhrWarnTimeout);
        // Append the request to display in the loading
        this.xhrs = [...this.xhrs, config];
        this.xhrsOutOf = [...this.xhrsOutOf, config];
      }
    },
    /** Handle the end of an XHR */
    xhrEnded(response) {
      const { config } = response;
      if (
        response.isAxiosError
        && response.response
        && response.response.status === 0
        && response.code === 'ERR_NETWORK'
      ) {
        // Network error
        this.promptNetworkUnavailable();
      }

      if (config) {
        clearTimeout(this.timeoutsPerXhrIds[`${config.xhrUniqueID}`]);
        delete this.timeoutsPerXhrIds[`${config.xhrUniqueID}`];
        // Remove the XHR from the main array, to display in the laoding
        this.xhrs = [...this.xhrs.filter(cfg => cfg.xhrUniqueID !== config.xhrUniqueID)];
        // Remove from the slow XHRs if it has been appended to the array
        this.slowXhrs = [...this.slowXhrs.filter(cfg => cfg.xhrUniqueID !== config.xhrUniqueID)];
        if (this.xhrs.length === 0) {
          this.xhrsOutOf = [];
        }
      }
    },
    /**
     * Enable offline mode. Turn off the network access and prevent axios requests to go through.
     * Also 'remember' creation and modification requests to apply them when the user wants to sync.
     */
    enableOfflineMode() {
      if (this.offlineMode) {
        return;
      }

      // Cancel current XHRs
      this.xhrs.forEach((xhr) => {
        if (xhr.controller && xhr.controller.abort instanceof Function) {
          xhr.controller.abort();
        }
      });

      // Append the XHR that need syncing in xhrsToSync
      this.xhrsToSync = [
        ...this.xhrs
          .map(xhr => urlMatchesInfoObject(xhr))
          .filter(xhr => xhr.info !== undefined),
      ];

      // Empty the xhrs arrays
      this.xhrs = [];
      this.xhrsOutOf = [];
      this.slowXhrs = [];
      Object.keys(this.timeoutsPerXhrIds).forEach((uid) => {
        clearTimeout(this.timeoutsPerXhrIds[`${uid}`]);
        delete this.timeoutsPerXhrIds[`${uid}`];
      });

      this.$store.commit(`device/${ENABLE_OFFLINE_MODE}`, true);
    },
    /**
     * Prompts the user to start the synchronization and
     * sync all the data that need syncing in a modal, to inform the user about what is happening
     * Then, if everything worked correctly, prompt the user to validate and turn off offline mode.
     */
    promptSyncOfflineMode() {
      this.promptSync = true;
    },
    /** Sync the data that need syncing */
    doSyncOfflineMode() {
      this.promptSync = false;
      this.syncing = true;

      this.xhrsToSyncPromises = this.xhrsToSync.map((xhr) => {
        this.$set(xhr, 'syncing', true);
        this.$set(xhr, 'syncingError', false);
        xhr.controller = new AbortController();
        xhr.signal = xhr.controller.signal;

        return new Promise((resolve, reject) => {
          this.$http(xhr)
            .then(() => {
              xhr.syncing = false;
              resolve();
            })
            .catch((err) => {
              xhr.syncing = false;
              xhr.syncingError = true;
              if (axios.isCancel(err)) {
                reject();
              } else {
                resolve();
              }
            });
        });
      });
      Promise.all(this.xhrsToSyncPromises)
        .then(() => {
          this.syncing = false;
          this.syncingOver = true;
        });
    },
    /** Turn off offline mode */
    disableOfflineMode() {
      this.$store.commit(`device/${ENABLE_OFFLINE_MODE}`, false);
      this.resetCurrentPage();
      this.syncing = false;
      this.syncingOver = false;
      this.promptSync = false;
    },
    /** Cancel prompting the synchronization */
    dontSyncOfflineMode() {
      this.syncing = false;
      this.syncingOver = false;
      this.promptSync = false;
    },
    /** Handle clicking on the close button of the sync modal */
    closeSyncModal() {
      if (this.promptSync) {
        this.dontSyncOfflineMode();
      } else if (this.syncingOver) {
        this.disableOfflineMode();
      }
    },
    /** Cancel the synchronization */
    cancelSync() {
      this.xhrsToSync.forEach((xhr) => {
        if (xhr.controller && xhr.controller.abort instanceof Function) {
          xhr.controller.abort();
        }
      });
      this.syncing = false;
      this.syncingOver = false;
      this.promptSync = true;
    },
    /** Reset the page entirely */
    resetCurrentPage() {
      const { fullPath } = this.$router.currentRoute;
      const replacement = fullPath.includes('/meeting-infos') ? '/pending' : '/meeting-infos';
      this.$router.replace(replacement);
      this.$nextTick(() => {
        this.$router.replace(fullPath);
      });
    },
    /** Prompts the user that we received hints that the network might be unavailable */
    promptNetworkUnavailable() {
      this.promptNetworkSeemsUnavailable = true;
      clearTimeout(this.promptNetworkUnavailableTimeout);
      this.promptNetworkUnavailableTimeout = setTimeout(() => {
        this.promptNetworkSeemsUnavailable = false;
      }, 15000);
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.network-manager {
  width: 0;
  height: 0;

  &__head {
    position: absolute;
    top: 0;
    // top: $header-height;
    left: $nav-width;
    right: 0;
    min-height: 1.7rem;
    font-size: 0.8rem;
    font-weight: 700;
    color: white;
    padding: 0 .5rem;

    &--warn {
      background: $color-orange-warn;
    }

    &--offline {
      background: $color-dark-red-bad;
    }

    &__wrapper {
      justify-content: space-between;
      width: 100%;

      button {
        border: none;
        background-color: rgba(255, 255, 255, 0.3);
        border-radius: $medium-border-radius;
        font-weight: 700;
        color: white;
        min-height: 1.3rem;
      }
    }

    .icon {
      width: 1rem;
      height: 1rem;
      margin-left: 1rem;
    }
  }

  /* Loading animation */
  &__loading {
    width: 60px;
    height: 60px;
    border-radius: 50px;
    position: absolute;
    bottom: .5rem;
    left: calc(#{$nav-width} + .5rem);
    z-index: 12000;
    box-shadow: 0px 0px 1rem .5rem #ffffff;
    background: #ffffff;

    img {
      position: absolute;
      top: 7px;
      left: 7px;
      width: 46px;
      height: 46px;
      animation: rotation 2s infinite linear;
      -webkit-animation: rotation 2s infinite linear;
    }

    &__number {
      font-size: 0.75rem;
      opacity: 0.7;
      position: absolute;
      top: 50%;
      left: 50%;
      white-space: nowrap;
      transform: translate(-50%, -50%);
    }
  }

  &__sync-modal {

    &.modal.dark-overlay .dark-overlay-content {
      height: auto;
    }

    &__prompt {
      padding: 2rem;
      text-align: center;

      &__buttons {
        margin-top: 2rem;

        button {
          margin: .5rem auto;
        }
      }
    }


    &__sync {
      padding: 2rem 4rem 2rem 2rem;

      &__xhr {
        margin-top: 1rem;
        height: 2.5rem;
        justify-content: space-between;

        &__loading {
          animation: rotation 2s infinite linear;
          -webkit-animation: rotation 2s infinite linear;

          img {
            width: 2rem;
            height: 2rem;
            display: block;
          }
        }

        &__done {
          color: $color-shizen-green;
          width: 1.3rem;
          height: 1.3rem;
        }
      }

      &__buttons {
        margin-top: 2rem;
      }
    }
  }
}

@media (max-width: $smartphone-landscape) {
  .network-manager {
    &__head {
      // top: $smartphone-header-height;
      left: $smartphone-nav-width;
    }

    &__loading {
      left: calc(#{$smartphone-nav-width} + 1.5rem);
    }
  }
}
</style>
